const SESSION_VERIFICATION_RE = /Session verification failed|Signature verification failed|SESSION_SETTLEMENT_FAILED|Forbidden:\s*Session/i;
let cleanupInFlight: Promise<void> | null = null;

export const PENDING_WALLET_REDIRECT_KEY = "robotdex.pendingWalletRedirect";

export function isSessionVerificationError(error: unknown): boolean {
  const err = error as { message?: string; reason?: string; code?: string | number } | undefined;
  const text = [err?.message, err?.reason, err?.code, String(error)].filter(Boolean).join(" ");
  return SESSION_VERIFICATION_RE.test(text);
}

export function markPendingWalletRedirect() {
  try {
    sessionStorage.setItem(PENDING_WALLET_REDIRECT_KEY, "1");
  } catch {
    // ignore storage failures
  }
}

export function consumePendingWalletRedirect(): boolean {
  try {
    const value = sessionStorage.getItem(PENDING_WALLET_REDIRECT_KEY);
    if (value) sessionStorage.removeItem(PENDING_WALLET_REDIRECT_KEY);
    return !!value;
  } catch {
    return false;
  }
}

export function clearPendingWalletRedirect() {
  try {
    sessionStorage.removeItem(PENDING_WALLET_REDIRECT_KEY);
  } catch {
    // ignore storage failures
  }
}

export async function clearAllAuthAndWalletSessions() {
  if (cleanupInFlight) return cleanupInFlight;
  cleanupInFlight = doClearAllAuthAndWalletSessions().finally(() => {
    cleanupInFlight = null;
  });
  return cleanupInFlight;
}

async function doClearAllAuthAndWalletSessions() {
  clearPendingWalletRedirect();

  try {
    const [{ disconnect }, { getWagmiConfig }] = await Promise.all([
      import("@wagmi/core"),
      import("@/lib/wallet"),
    ]);
    await disconnect(getWagmiConfig()).catch(() => undefined);
  } catch {
    // ignore in-memory wallet disconnect failures
  }

  try {
    const { closeAppKit } = await import("@/lib/wallet");
    await closeAppKit();
  } catch {
    // ignore wallet modal cleanup failures
  }

  try {
    const { supabase } = await import("@/integrations/supabase/client");
    await supabase.auth.signOut().catch(() => undefined);
    await supabase.auth.signOut({ scope: "local" }).catch(() => undefined);
  } catch {
    // ignore backend auth cleanup failures
  }

  const storagePrefixes = [
    "wc@2:",
    "wagmi.",
    "wagmi:",
    "@w3m/",
    "@appkit/",
    "W3M_",
    "WALLETCONNECT_",
    "reown.",
    "@reown/",
    "sb-",
    "supabase.auth.",
    "robotdex.",
  ];

  try {
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key && storagePrefixes.some((prefix) => key.startsWith(prefix))) keys.push(key);
    }
    keys.forEach((key) => localStorage.removeItem(key));
  } catch {
    // ignore storage failures
  }

  try {
    const keys: string[] = [];
    for (let i = 0; i < sessionStorage.length; i += 1) {
      const key = sessionStorage.key(i);
      if (key && storagePrefixes.some((prefix) => key.startsWith(prefix))) keys.push(key);
    }
    keys.forEach((key) => sessionStorage.removeItem(key));
  } catch {
    // ignore storage failures
  }

  try {
    const cookies = document.cookie ? document.cookie.split(";") : [];
    for (const raw of cookies) {
      const name = raw.split("=")[0]?.trim();
      if (!name) continue;
      const paths = ["/", window.location.pathname];
      const hostParts = window.location.hostname.split(".");
      const domains = ["", window.location.hostname, `.${window.location.hostname}`];
      if (hostParts.length > 2) domains.push(`.${hostParts.slice(-2).join(".")}`);
      for (const path of paths) {
        for (const domain of domains) {
          const domainAttr = domain ? `; domain=${domain}` : "";
          document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${path}${domainAttr}`;
        }
      }
    }
  } catch {
    // ignore cookie cleanup failures
  }

  try {
    const databases = (indexedDB as unknown as { databases?: () => Promise<{ name?: string }[]> }).databases;
    if (typeof databases === "function") {
      const dbs = await databases.call(indexedDB);
      for (const db of dbs) {
        if (db.name && /wc@2|walletconnect|w3m|appkit|reown|supabase/i.test(db.name)) {
          indexedDB.deleteDatabase(db.name);
        }
      }
    }
  } catch {
    // ignore IndexedDB cleanup failures
  }
}